/**
 * Templates list. "Skapa avtal" drafts a contract pinned to that
 * template version and routes to the editor.
 *
 * Group routing: the active-group header is the canonical source — the
 * create call sends no body `group_id`, the backend resolves from the
 * header. A multi-group user picks via the inline GroupPicker (or the
 * persistent header dropdown); single-group users see no inline picker
 * since the header chip is already authoritative.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTemplateVersion } from '@hyranai/regta-templates';
import { tenantApi, type DocumentEngine, type TemplateListRow } from '../api.js';
import { LegacyCreateLink, contractEditorPath, defaultEngine } from '../components/DocumentEngine.js';
import { useTenantSession } from '../auth-context.js';
import { ActiveGroupNotice, useActiveGroup } from '../components/GroupPicker.js';
import { TemplateFavoriteStar } from '../components/TemplateFavoriteStar.js';
import { ChatPanel } from '../components/ChatPanel.js';
import { useInvalidate, useTenantQuery } from '../query.js';
import { usePageTitle } from '../use-page-title.js';

type GroupKey =
  | 'bostad'
  | 'lokal'
  | 'hyressattning'
  | 'parkering'
  | 'ovrigt';

type SubKey =
  | 'kollektiv'
  | 'forhandlingsordning'
  | 'forhandlingsframstallning'
  | 'forhandlingsprotokoll'
  | 'forhandlingsoverenskommelser'
  | 'hyressattningsavtal'
  | 'tidsbestamda';

const GROUP_LABELS: Record<GroupKey, string> = {
  bostad: 'Bostadshyra',
  lokal: 'Lokalhyra',
  hyressattning: 'Bostadshyressättning',
  parkering: 'Parkering och garage',
  ovrigt: 'Övriga mallar',
};

// Top-of-page huvudkategori filter. "Alla mallar" (= no filter) is the
// default; the four buckets mirror GROUP_LABELS. `ovrigt` is intentionally
// not offered as its own chip — those templates surface only under "Alla
// mallar".
const CATEGORY_FILTERS: { key: GroupKey | 'all'; label: string }[] = [
  { key: 'all', label: 'Alla mallar' },
  { key: 'bostad', label: GROUP_LABELS.bostad },
  { key: 'hyressattning', label: GROUP_LABELS.hyressattning },
  { key: 'lokal', label: GROUP_LABELS.lokal },
  { key: 'parkering', label: GROUP_LABELS.parkering },
];

// Ordered list of HYRESSÄTTNINGSAVTAL sub-buckets. Empty sub-buckets
// are hidden until templates exist for them; the order here drives the
// display order when they do.
const HYRESSATTNING_SUBS: { key: SubKey; label: string }[] = [
  { key: 'kollektiv', label: 'Kollektiv hyressättning' },
  { key: 'forhandlingsordning', label: 'Förhandlingsordning' },
  { key: 'forhandlingsframstallning', label: 'Förhandlingsframställning' },
  { key: 'forhandlingsprotokoll', label: 'Förhandlingsprotokoll' },
  {
    key: 'forhandlingsoverenskommelser',
    label: 'Förhandlingsöverenskommelser',
  },
  { key: 'hyressattningsavtal', label: 'Hyressättningsavtal' },
  { key: 'tidsbestamda', label: 'Tidsbestämda hyressättningsavtal' },
];

const GROUP_ORDER: GroupKey[] = [
  'bostad',
  'hyressattning',
  'lokal',
  'parkering',
  'ovrigt',
];

/**
 * Map a template's backend `category` string onto the editor's grouping
 * scheme. Backend categories were defined per-template and aren't
 * presentation-aware, so the mapping lives here.
 */
function classify(category: string): { group: GroupKey; sub?: SubKey } {
  switch (category) {
    case 'Bostadshyra':
      return { group: 'bostad' };
    case 'Lokalhyra':
      return { group: 'lokal' };
    case 'Bilplats':
    case 'Garage':
      return { group: 'parkering' };
    case 'Bostadshyressättningsavtal':
    case 'Bostadshyressättningsavtal-3part':
      return { group: 'hyressattning', sub: 'forhandlingsordning' };
    case 'Forhandlingsoverenskommelse-2part':
    // Förhandlingsöverenskommelse 3-part is a bostadshyressättnings-
    // dokument — without this case it fell through to "Övriga mallar".
    case 'Forhandlingsoverenskommelse-3part':
      return { group: 'hyressattning', sub: 'forhandlingsoverenskommelser' };
    // Non-avtal helper documents tied to a bostad lease live under Bostadshyra.
    case 'Ändring av hyresvillkor':
    case 'Ombyggnad och ändring':
    case 'Studentbostad':
      return { group: 'bostad' };
    default:
      return { group: 'ovrigt' };
  }
}

export function TemplatesPage() {
  usePageTitle('Mallar');
  const navigate = useNavigate();
  const { token } = useTenantSession();
  const { activeGroupId } = useActiveGroup();
  const invalidate = useInvalidate();

  const { data, error, isLoading } = useTenantQuery(
    'templates.list',
    () => tenantApi.templates.list(),
  );
  // AI contract assistant (ADR-0076). The entry point only appears when the
  // assistant is provisioned in this environment (Bedrock configured).
  const [chatOpen, setChatOpen] = useState(false);
  const { data: chatStatus } = useTenantQuery('chat.status', () =>
    tenantApi.chat.status(),
  );
  // Connected-only templates (clean tilläggsavtal frames, ADR-0061) are
  // hidden from the standalone picker — they're created from a parent
  // contract's "Kopplade avtal" panel, which reads templates.list directly.
  const templates = useMemo(() => {
    const rows = data?.templates ?? null;
    if (!rows) return null;
    return rows.filter((t) => {
      const mod = getTemplateVersion(t.template_key, t.version);
      return !mod?.metadata.connectedOnly;
    });
  }, [data]);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [writeErr, setWriteErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<GroupKey | 'all'>('all');
  const [query, setQuery] = useState('');

  // Free-text search across name/beskrivning/kategori, AND-combined with
  // the huvudkategori filter. The library is small, so filtering is purely
  // client-side.
  const filtered = useMemo(() => {
    if (!templates) return null;
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) =>
      `${t.name} ${t.description ?? ''} ${t.category}`
        .toLowerCase()
        .includes(q),
    );
  }, [templates, query]);

  const grouped = useMemo(() => {
    if (!filtered) return null;
    const groups: Record<GroupKey, TemplateListRow[]> = {
      bostad: [],
      lokal: [],
      hyressattning: [],
      parkering: [],
      ovrigt: [],
    };
    const subs: Partial<Record<SubKey, TemplateListRow[]>> = {};
    for (const t of filtered) {
      const { group, sub } = classify(t.category);
      groups[group].push(t);
      if (sub) {
        (subs[sub] ??= []).push(t);
      }
    }
    return { groups, subs };
  }, [filtered]);

  const favorites = useMemo(
    () => filtered?.filter((t) => t.favorited) ?? [],
    [filtered],
  );

  const createDraft = async (t: TemplateListRow, engine: DocumentEngine = defaultEngine(t)) => {
    if (!token) return;
    if (!activeGroupId) {
      setWriteErr('Välj en grupp innan du skapar avtalet.');
      return;
    }
    setBusyId(t.version_id);
    setWriteErr(null);
    try {
      const r = await tenantApi.contracts.create_from_template({
        template_version_id: t.version_id,
        // group_id elided — header is authoritative.
        ...(engine === 'ir' ? { engine } : {}),
      });
      await invalidate('contracts.list');
      navigate(contractEditorPath(r.contract_id, engine));
    } catch (e) {
      setWriteErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const renderCard = (t: TemplateListRow) => (
    <div
      key={t.version_id}
      className="flex h-full flex-col justify-between gap-3 rounded-lg border border-stone-200 bg-white p-4 shadow-sm transition hover:border-stone-300 hover:shadow"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div
            className="truncate text-sm font-medium text-stone-900"
            title={t.name}
          >
            {t.name}
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-stone-500">
            <span>
              {t.category} · {t.version}
            </span>
            {t.status === 'draft' && (
              <span
                className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
                title="Förhandsvisning — mallen är inte allmänt tillgänglig ännu"
              >
                Utkast
              </span>
            )}
          </div>
          {t.description && (
            <div className="mt-2 line-clamp-3 text-xs text-stone-600">
              {t.description}
            </div>
          )}
        </div>
        <TemplateFavoriteStar
          templateId={t.template_id}
          favorited={t.favorited}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void createDraft(t)}
          disabled={busyId === t.version_id || !activeGroupId}
          className="self-start rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          {busyId === t.version_id ? 'Skapar…' : 'Skapa avtal'}
        </button>
        {t.ir_available && (
          <LegacyCreateLink
            onClick={() => void createDraft(t, 'legacy')}
            disabled={busyId === t.version_id || !activeGroupId}
          />
        )}
      </div>
    </div>
  );

  const renderGrid = (rows: TemplateListRow[]) => (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {rows.map(renderCard)}
    </div>
  );

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-sm font-medium text-stone-500">Mallar</h2>
        <div className="flex items-center gap-3">
          {chatStatus?.available && (
            <button
              type="button"
              onClick={() => setChatOpen(true)}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
            >
              Avtalsassistent
            </button>
          )}
          <ActiveGroupNotice prefix="Skapas i" />
        </div>
      </div>
      {chatOpen && <ChatPanel onClose={() => setChatOpen(false)} />}
      {(writeErr || error) && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {writeErr ?? (error as Error).message}
        </div>
      )}
      {isLoading || grouped === null ? (
        <div className="text-sm text-stone-400">loading…</div>
      ) : templates && templates.length === 0 ? (
        <div className="text-sm text-stone-400">no templates available</div>
      ) : (
        <div className="space-y-8">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Sök bland mallar…"
            className="w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm placeholder-stone-400 focus:border-stone-500 focus:outline-none"
          />

          {/* Huvudkategori-filter — välj en kategori eller "Alla mallar". */}
          <div className="flex flex-wrap gap-2">
            {CATEGORY_FILTERS.map((f) => {
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  aria-pressed={active}
                  className={
                    'rounded-full px-3 py-1.5 text-xs font-medium transition ' +
                    (active
                      ? 'bg-stone-900 text-white'
                      : 'border border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:text-stone-900')
                  }
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* Favoriter shows only in the unfiltered view. */}
          {filter === 'all' && favorites.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-600">
                Favoriter
              </h3>
              {renderGrid(favorites)}
            </div>
          )}

          {(() => {
            const order =
              filter === 'all'
                ? GROUP_ORDER
                : GROUP_ORDER.filter((k) => k === filter);
            const visible = order.filter((k) => grouped.groups[k].length > 0);
            if (visible.length === 0) {
              return (
                <div className="text-sm text-stone-400">
                  {query.trim()
                    ? 'Inga mallar matchar din sökning.'
                    : 'Inga mallar i den här kategorin ännu.'}
                </div>
              );
            }
            return visible.map((key) => {
              const rows = grouped.groups[key];
              if (key === 'hyressattning') {
                const usedSubs = HYRESSATTNING_SUBS.filter(
                  (s) => (grouped.subs[s.key]?.length ?? 0) > 0,
                );
                return (
                  <div key={key} className="space-y-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                      {GROUP_LABELS[key]}
                    </h3>
                    <div className="space-y-5">
                      {usedSubs.map((s) => (
                        <div key={s.key} className="space-y-2">
                          <h4 className="text-xs font-medium text-stone-600">
                            {s.label}
                          </h4>
                          {renderGrid(grouped.subs[s.key] ?? [])}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <div key={key} className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                    {GROUP_LABELS[key]}
                  </h3>
                  {renderGrid(rows)}
                </div>
              );
            });
          })()}
        </div>
      )}
    </section>
  );
}
